import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Checkbox } from '../../../components/Checkbox';
import { DefaultSelectBox, ISelectBoxOption } from '../../../components/SelectBox';
import { TextInput } from '../../../components/TextInput';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { baseStyleSheet, bs } from '../../../styles/styles';

interface OpportunityStepFormProps {
	/**
	 * The currently selected board.
	 */
	selectedBoard: Api.IBoard | null;
	/**
	 * The currently selected stage.
	 */
	selectedStage: Api.IBoardStage | null;
	/**
	 * Current deal size value (as string for input).
	 */
	dealSize: string;
	/**
	 * Whether to update existing opportunity instead of creating new.
	 */
	updateExisting: boolean;
	/**
	 * Available board options for selection.
	 */
	boardOptions: ISelectBoxOption<Api.IBoard>[] | undefined;
	/**
	 * Available stage options for the selected board.
	 */
	stageOptions: ISelectBoxOption<Api.IBoardStage>[];
	/**
	 * Whether the form can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether data is being saved.
	 */
	isBusy: boolean;
	/**
	 * Whether boards are still loading.
	 */
	isLoadingBoards: boolean;
	/**
	 * Unique key for the form.
	 */
	baseKey: string;
	/**
	 * Callback when board selection changes.
	 */
	onBoardChange: (board: Api.IBoard, firstStage: Api.IBoardStage | null) => void;
	/**
	 * Callback when stage selection changes.
	 */
	onStageChange: (stage: Api.IBoardStage) => void;
	/**
	 * Callback when deal size changes.
	 */
	onDealSizeChange: (size: string) => void;
	/**
	 * Callback when deal size input blurs (trigger save).
	 */
	onDealSizeBlur: () => void;
	/**
	 * Callback when update existing checkbox changes.
	 */
	onUpdateExistingChange: (checked: boolean) => void;
	/**
	 * Callback to clear error messages.
	 */
	onClearError: () => void;
}

/**
 * Form component for editing opportunity automation step content.
 * Displays board and stage selectors with optional deal size input.
 */
export function OpportunityStepForm({
	selectedBoard,
	selectedStage,
	dealSize,
	updateExisting,
	boardOptions,
	stageOptions,
	canEdit,
	isBusy,
	isLoadingBoards,
	baseKey,
	onBoardChange,
	onStageChange,
	onDealSizeChange,
	onDealSizeBlur,
	onUpdateExistingChange,
	onClearError,
}: OpportunityStepFormProps) {
	const isDisabled = !canEdit || isBusy;

	const handleBoardSelectionChanged = (option: ISelectBoxOption<Api.IBoard>) => {
		if (selectedBoard && option.value.id === selectedBoard.id) {
			return;
		}
		const firstStage = option.value.stages?.[0] || null;
		onBoardChange(option.value, firstStage);
	};

	const handleStageSelectionChanged = (option: ISelectBoxOption<Api.IBoardStage>) => {
		if (selectedStage && option.value.id === selectedStage.id) {
			return;
		}
		onStageChange(option.value);
	};

	const handleDealSizeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		onDealSizeChange(e.target.value);
		onClearError();
	};

	const handleUpdateExistingChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		onUpdateExistingChange(e.target.checked);
	};

	const selectedBoardOption = boardOptions?.find(x => x.value.id === selectedBoard?.id);
	const selectedStageOption = stageOptions.find(x => x.value.id === selectedStage?.id) ?? stageOptions[0];

	return (
		<>
			<Checkbox
				id={`automation-opportunity-step-updateExisting-checkbox-${baseKey}`}
				checked={updateExisting}
				type='large'
				onChange={handleUpdateExistingChanged}
				className={css(styleSheet.updateExistingLabel, baseStyleSheet.mt3)}
				disabled={isDisabled}
			>
				<div>
					<p className={css(styleSheet.noMargin, styleSheet.checkboxText)}>
						Update an existing opportunity instead of creating a new one.
					</p>
				</div>
			</Checkbox>

			<div className={css(styleSheet.fieldLabel)}>
				Select an opportunity board: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<DefaultSelectBox
				className={css(styleSheet.typeSelect)}
				disabled={isDisabled || isLoadingBoards}
				onSelectionChanged={handleBoardSelectionChanged}
				options={boardOptions}
				triggerClassName={css(styleSheet.opportunityDropdown)}
				optionStyles={[styleSheet.typeSelectMenuItem]}
				placeholder={
					<span className={css(bs.textTitles, bs.textSm)}>
						{isLoadingBoards ? 'Loading boards...' : 'Select a choice below...'}
					</span>
				}
				contentClassName={css(styleSheet.typeSelectContent)}
				selectedOption={selectedBoardOption}
			/>

			<div className={css(styleSheet.fieldLabel, styleSheet.stageLabel)}>
				Select a stage: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<DefaultSelectBox
				className={css(styleSheet.typeSelect)}
				disabled={isDisabled || !selectedBoard}
				onSelectionChanged={handleStageSelectionChanged}
				options={stageOptions}
				triggerClassName={css(styleSheet.opportunityDropdown)}
				optionStyles={[styleSheet.typeSelectMenuItem]}
				placeholder={<span className={css(bs.textTitles, bs.textSm)}>Select a choice below...</span>}
				contentClassName={css(styleSheet.typeSelectContent)}
				selectedOption={selectedStageOption}
			/>

			{!updateExisting && (
				<>
					<div className={css(styleSheet.fieldLabel, styleSheet.stageLabel)}>Amount: &nbsp;</div>
					<TextInput
						className={css(styleSheet.emailNameInput)}
						disabled={isDisabled}
						inputId={`automation-opportunity-step-amount-input-${baseKey}`}
						key={`${baseKey}-amount-input`}
						leftAccessory={<span className={css(styleSheet.dollar)}>$</span>}
						onBlur={onDealSizeBlur}
						onChange={handleDealSizeChanged}
						onFocus={onClearError}
						placeholder='0.00'
						type='text'
						value={dealSize || ''}
					/>
				</>
			)}
		</>
	);
}
